import { scopedCustomCss, themeCss, type ThemeSettings } from '@acme/ui'
import { onBeforeUnmount, watchEffect, type Ref } from 'vue'
import { useTheme, type ThemePreference } from './theme'

export const TENANT_THEME_SCOPE = ':root[data-tenant-theme]'
const STYLE_ID = 'tenant-theme'
// The last theme applied on this address, so the next page load (a reload, the sign-in callback)
// starts with the institution's colours instead of the default grey while the app boots.
const CACHE_KEY = 'aubaine.tenantTheme'

function applyCss(css: string) {
  let style = document.getElementById(STYLE_ID) as HTMLStyleElement | null
  if (!style) {
    style = document.createElement('style')
    style.id = STYLE_ID
    document.head.appendChild(style)
  }
  // textContent, never innerHTML: the CSS is data (the backend already refuses "<").
  style.textContent = css
  document.documentElement.setAttribute('data-tenant-theme', '')
}

function cache(css: string | null) {
  try {
    if (css) localStorage.setItem(CACHE_KEY, css)
    else localStorage.removeItem(CACHE_KEY)
  } catch {
    // Storage blocked (private window): the page just starts with the default look.
  }
}

// Call before the app mounts: puts back the theme cached by useTenantTheme, if any (useTenantTheme
// replaces or clears it as soon as the real one is known).
export function restoreCachedTenantTheme() {
  try {
    const css = localStorage.getItem(CACHE_KEY)
    if (css) applyCss(css)
  } catch {
    // Same as above.
  }
}

export interface AppliedTenantTheme {
  settings: ThemeSettings
  customCss?: string | null
  // null = the tenant never saved a theme: the default look stays.
  updatedAtUtc?: string | null
}

// Applies the tenant's saved theme (Configurações › Marca › Tema do cliente) to the whole page —
// dialogs and toasts are teleported to <body> — as data-tenant-theme on <html> plus a <style> with
// the tokens, the component defaults and the custom CSS (last, so it wins). Used by the portal and
// by the entry screens, so both wear the same theme.
// pending: the theme is still being fetched, so what is on screen (the cached theme) stays meanwhile.
export function useTenantTheme(theme: Ref<AppliedTenantTheme | null | undefined>, pending?: Ref<boolean>) {
  const { setInitial } = useTheme()

  function clear() {
    document.documentElement.removeAttribute('data-tenant-theme')
    document.getElementById(STYLE_ID)?.remove()
  }

  watchEffect(() => {
    const current = theme.value
    if (pending?.value) return
    if (!current?.updatedAtUtc) {
      clear()
      cache(null)
      return
    }
    const css = [themeCss(current.settings, { scope: TENANT_THEME_SCOPE }), scopedCustomCss(current.customCss ?? '', TENANT_THEME_SCOPE)]
      .filter(Boolean)
      .join('\n')
    applyCss(css)
    cache(css)
    setInitial(current.settings.initialTheme as ThemePreference)
  })

  onBeforeUnmount(clear)
}
